// src/utils/pdfTripSheet.js
import { jsPDF } from "jspdf";

/**
 * Optimized Two-Column Trip Sheet Generator
 * Uses the right half of the A4 page before creating a new sheet to save paper.
 */
export function generateTripSheetPdf(orderId, order, items, skuMaster, totals) {
  const pdf = new jsPDF("portrait", "mm", "a4");
  
  const leftX = 10;
  const rightX = 105; // Use the right half starting at 105mm
  const columnWidth = 95;
  const bottomLimit = 270; // A4 height is 297mm
  const topTableY = 60; // Standard table start position after header

  let currentX = leftX;
  let y = 15;

  // --- HELPER: DRAW COLUMN HEADERS ---
  const drawHeader = (pdfObj, x, yPos) => {
    pdfObj.setFont("helvetica", "bold");
    pdfObj.setFontSize(10);
    pdfObj.rect(x, yPos - 5, columnWidth, 8);
    pdfObj.text("S.No", x + 2, yPos);
    pdfObj.text("Product Name", x + 12, yPos);
    pdfObj.text("Qty", x + columnWidth - 5, yPos, { align: "right" });
    pdfObj.setFont("helvetica", "normal");
  };

  // --- MAIN PAGE HEADER (Always on P1, top left area) ---
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text("TRIP SHEET", leftX + columnWidth / 2, y, { align: "center" });

  y += 12;
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  pdf.text("Name :", leftX, y);
  pdf.text(order.partyName || "-", leftX + 25, y);

  pdf.text("Date :", leftX, y + 8);
  const finalDate = order.orderDateOnly
    ? new Date(order.orderDateOnly).toLocaleDateString("en-IN")
    : "-";
  pdf.text(finalDate, leftX + 25, y + 8);

  y = topTableY; // Move to table start
  drawHeader(pdf, currentX, y);
  y += 10;

  // --- SKU LOOP (With Column & Page Wrapping) ---
  let index = 1;
  const skus = Object.keys(items).filter(sku => Number(items[sku] || 0) > 0);

  skus.forEach((sku) => {
    // Check if we hit the bottom
    if (y > bottomLimit) {
      if (currentX === leftX) {
        // Switch to RIGHT column on same page
        currentX = rightX;
        y = topTableY;
        drawHeader(pdf, currentX, y);
        y += 10;
      } else {
        // Both columns full, add NEW PAGE
        pdf.addPage();
        currentX = leftX;
        y = 20; // Start higher on subsequent pages since there is no main header
        drawHeader(pdf, currentX, y);
        y += 10;
      }
    }

    const qty = Number(items[sku] || 0);
    const name = skuMaster[sku]?.Name || sku;

    pdf.setFontSize(9);
    // Row border
    pdf.rect(currentX, y - 5, columnWidth, 8);
    pdf.text(String(index), currentX + 2, y);
    
    // Auto-truncate extremely long names for single-column fit
    const truncatedName = name.length > 40 ? name.substring(0, 37) + "..." : name;
    pdf.text(truncatedName, currentX + 12, y);
    
    pdf.text(qty.toString(), currentX + columnWidth - 5, y, { align: "right" });

    y += 10;
    index++;
  });

  // --- FOOTER (Placed after last item) ---
  // Ensure we don't bleed off page with the footer
  if (y > 240) {
    if (currentX === leftX) {
      currentX = rightX;
      y = topTableY;
    } else {
      pdf.addPage();
      currentX = leftX;
      y = 20;
    }
  }

  y += 5;
  pdf.setFillColor(245, 245, 245);
  pdf.roundedRect(currentX, y, columnWidth, 18, 2, 2, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text("Total Qty :", currentX + 3, y + 7);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(totals.totalQty || 0), currentX + 35, y + 7);

  pdf.setFont("helvetica", "bold");
  pdf.text("Total Amount :", currentX + 3, y + 14);
  pdf.setFont("helvetica", "normal");
  pdf.text(String(totals.grandTotal?.toFixed(2) || "0.00"), currentX + 35, y + 14);

  y += 30;
  pdf.setFont("helvetica", "bold");
  pdf.text("Received By:", currentX, y);
  pdf.line(currentX + 25, y + 1, currentX + columnWidth - 5, y + 1);

  return pdf;
}
